import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersAPI } from '../utils/api';
import { User } from '../types';
import { ArrowRightLeft, Loader2, Search } from 'lucide-react';

interface Tenant {
  id: string;
  nome: string;
  slug: string;
}

interface ImpersonateModalProps {
  isOpen: boolean;
  tenant: Tenant | null;
  onClose: () => void;
  onConfirm: (tenantId: string, userId: string) => void;
  isLoading?: boolean;
}

export default function ImpersonateModal({
  isOpen,
  tenant,
  onClose,
  onConfirm,
  isLoading = false,
}: ImpersonateModalProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [search, setSearch] = useState('');

  // Fetch users of the tenant
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['tenant-users', tenant?.id],
    queryFn: async () => {
      const response = await usersAPI.getUsers({
        tenant_id: tenant!.id,
        limit: 100,
        is_active: true,
      });
      return response.data;
    },
    enabled: isOpen && !!tenant?.id,
  });

  const users: User[] = usersData?.data || [];

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const term = search.toLowerCase();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(term) ||
        (u.nome || '').toLowerCase().includes(term) ||
        (u.cognome || '').toLowerCase().includes(term) ||
        u.name.toLowerCase().includes(term)
    );
  }, [users, search]);

  // Default to first admin when users load
  useEffect(() => {
    if (users.length > 0 && !selectedUserId) {
      const admin = users.find((u) => u.role === 'admin');
      setSelectedUserId(admin?.id || users[0].id);
    }
  }, [users, selectedUserId]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedUserId('');
      setSearch('');
    }
  }, [isOpen]);

  if (!isOpen || !tenant) return null;

  const roleLabels: Record<string, string> = {
    admin: 'Admin',
    manager: 'Manager',
    commerciale: 'Commerciale',
    viewer: 'Viewer',
    cliente: 'Cliente',
  };

  const roleBadgeColors: Record<string, string> = {
    admin: 'bg-red-100 text-red-700',
    manager: 'bg-blue-100 text-blue-700',
    commerciale: 'bg-green-100 text-green-700',
    viewer: 'bg-gray-100 text-gray-700',
    cliente: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center gap-3 p-6 border-b border-gray-200">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <ArrowRightLeft className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Impersona Tenant</h3>
            <p className="text-sm text-gray-500">{tenant.nome}</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleziona l'utente da impersonare
          </label>

          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Caricamento utenti...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              Nessun utente attivo trovato in questo tenant.
            </div>
          ) : (
            <>
              {/* Search */}
              {users.length > 5 && (
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca utente..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* User list */}
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <label
                    key={user.id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedUserId === user.id ? 'bg-purple-50' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="impersonate-user"
                      value={user.id}
                      checked={selectedUserId === user.id}
                      onChange={() => setSelectedUserId(user.id)}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {user.name || `${user.nome || ''} ${user.cognome || ''}`.trim() || user.email}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        roleBadgeColors[user.role] || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {roleLabels[user.role] || user.role}
                    </span>
                  </label>
                ))}
                {filteredUsers.length === 0 && search && (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                    Nessun risultato per "{search}"
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            onClick={() => {
              if (selectedUserId && tenant) {
                onConfirm(tenant.id, selectedUserId);
              }
            }}
            disabled={isLoading || !selectedUserId || isLoadingUsers}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowRightLeft className="h-4 w-4" />
            )}
            Impersona
          </button>
        </div>
      </div>
    </div>
  );
}
